import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faSpinner,
  faCircleCheck,
  faEnvelope,
  faTriangleExclamation,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { platformCompanyService } from '@/services/platform-company.service';
import { usePlatformAuthStore } from '@/stores/platform-auth.store';
import { canPlatform } from '@/lib/platform-permissions';
import type { PlatformPlan, ProvisionCompanyResponse } from '@/types/platform-auth.types';
import { PLATFORM_ROUTES } from '@/routes/platform-routes';

/**
 * Phase 12.3G — Create Company.
 *
 * Design of record: `COMPANIES_DESIGN.md` v1.0 §5.3. A single-page form with a
 * review step, not a wizard — the field count is modest and a wizard adds
 * ceremony without reducing risk.
 *
 * ── There is no password field, and that is the point ──────────────────────
 * D-6: the platform administrator must never choose, see or transmit a
 * customer's password. This form collects owner IDENTITY only; the backend
 * creates the account with an unusable credential and emails the owner an
 * invitation with which they set their own. There is no hidden field, no
 * optional field, and no fallback — `ProvisionCompanyRequest` has no password
 * property, and the API rejects one outright.
 */

const SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*[a-z0-9]$/;

/** Mirrors the API's slug rule so the operator is told before the round trip. */
function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 63);
}

const TIMEZONES = ['UTC', 'Asia/Kolkata', 'Europe/London', 'America/New_York', 'Asia/Dubai'];
const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'Hindi' },
];
const CURRENCIES = ['USD', 'INR', 'GBP', 'EUR', 'AED'];

type FieldErrors = Partial<Record<string, string>>;

/** Pulls a useful message out of an axios error without leaking internals. */
function messageFrom(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as
      { error?: { message?: string }; message?: string | string[] } | undefined;
    if (body?.error?.message) return body.error.message;
    if (typeof body?.message === 'string') return body.message;
    if (Array.isArray(body?.message) && body.message.length > 0) return body.message.join('. ');
    if (!err.response) return 'The server could not be reached. No company was created.';
  }
  return err instanceof Error && err.message ? err.message : fallback;
}

export function CreateCompanyPage() {
  const navigate = useNavigate();
  const role = usePlatformAuthStore((s) => s.user?.role);
  const canCreate = canPlatform(role, 'platform:companies:create');

  const [step, setStep] = useState<'form' | 'review' | 'done'>('form');
  const [plans, setPlans] = useState<PlatformPlan[]>([]);
  const [plansError, setPlansError] = useState('');

  const [tenantName, setTenantName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [planId, setPlanId] = useState('');
  const [ownerFirstName, setOwnerFirstName] = useState('');
  const [ownerLastName, setOwnerLastName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [language, setLanguage] = useState('en');
  const [currency, setCurrency] = useState('USD');

  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [result, setResult] = useState<ProvisionCompanyResponse | null>(null);

  // The permission gate here is UI-only; the backend guard is the authority.
  useEffect(() => {
    if (!canCreate) navigate(PLATFORM_ROUTES.COMPANIES, { replace: true });
  }, [canCreate, navigate]);

  useEffect(() => {
    platformCompanyService
      .listPlans()
      .then((all) => {
        const active = all.filter((p) => p.isActive);
        setPlans(active);
        if (active.length === 0) {
          setPlansError('No active plans are available. A company cannot be created without one.');
        }
      })
      .catch(() =>
        setPlansError('Plans could not be loaded, so a company cannot be created right now.'),
      );
  }, []);

  // Slug follows the name until the operator edits it, then stops (§5.3).
  useEffect(() => {
    if (!slugTouched) setSlug(deriveSlug(tenantName));
  }, [tenantName, slugTouched]);

  const validate = (): FieldErrors => {
    const errors: FieldErrors = {};
    if (!tenantName.trim()) errors['tenantName'] = 'Company name is required.';
    else if (tenantName.trim().length > 255) errors['tenantName'] = 'Maximum 255 characters.';

    if (!slug.trim()) errors['slug'] = 'Slug is required.';
    else if (slug.length < 3 || slug.length > 63) errors['slug'] = 'Between 3 and 63 characters.';
    else if (!SLUG_PATTERN.test(slug))
      errors['slug'] =
        'Lowercase letters, digits and hyphens only, starting and ending with a letter or digit.';

    if (!planId) errors['planId'] = 'Select a plan.';
    if (!ownerFirstName.trim()) errors['ownerFirstName'] = 'Owner first name is required.';
    if (!ownerLastName.trim()) errors['ownerLastName'] = 'Owner last name is required.';
    if (!ownerEmail.trim()) errors['ownerEmail'] = 'Owner email is required.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(ownerEmail.trim()))
      errors['ownerEmail'] = 'Enter a valid email address.';

    return errors;
  };

  const goToReview = (e: FormEvent) => {
    e.preventDefault();
    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setFormError('');
    setStep('review');
  };

  const selectedPlan = useMemo(
    () => plans.find((p) => p.planId === planId) ?? null,
    [plans, planId],
  );

  const submit = async () => {
    setSubmitting(true);
    setFormError('');
    setFieldErrors({});
    try {
      const created = await platformCompanyService.provision({
        tenantName: tenantName.trim(),
        tenantSlug: slug.trim(),
        planId,
        ownerEmail: ownerEmail.trim(),
        ownerFirstName: ownerFirstName.trim(),
        ownerLastName: ownerLastName.trim(),
        timezone,
        language,
        currency,
      });
      setResult(created);
      setStep('done');
    } catch (err) {
      const status = axios.isAxiosError(err) ? err.response?.status : undefined;

      if (status === 409) {
        setFieldErrors({ slug: 'That slug is already taken.' });
        setStep('form');
      } else if (status === 404) {
        setFieldErrors({ planId: 'That plan is no longer available. Choose another.' });
        setStep('form');
      } else if (status === 400) {
        setStep('form');
        setFormError(messageFrom(err, 'Some details were rejected. Check the fields and retry.'));
      } else {
        // §5.3 — say plainly that nothing was created. An operator who fears a
        // half-made tenant will either hesitate or retry and create two.
        setFormError(
          `${messageFrom(err, 'The company could not be created.')} No company was created. You can safely try again.`,
        );
        setStep('form');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!canCreate) return null;

  // ── Success ───────────────────────────────────────────────────────────────
  if (step === 'done' && result) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="rounded-xl border border-border bg-card p-6 text-center">
          <FontAwesomeIcon icon={faCircleCheck} className="h-10 w-10 text-green-600" />
          <h1 className="mt-3 text-xl font-semibold text-foreground">
            {result.tenantName} has been created
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            The company is in <span className="font-medium">Trial</span>. Activating it is a
            separate, explicit decision.
          </p>
        </div>

        <div
          className={
            result.ownerInvitation.delivered
              ? 'rounded-xl border border-border bg-card p-5'
              : 'rounded-xl border border-amber-500/40 bg-amber-500/5 p-5'
          }
        >
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={result.ownerInvitation.delivered ? faEnvelope : faTriangleExclamation}
              className={
                result.ownerInvitation.delivered
                  ? 'mt-0.5 h-4 w-4 shrink-0 text-primary'
                  : 'mt-0.5 h-4 w-4 shrink-0 text-amber-600'
              }
            />
            <div className="min-w-0">
              {result.ownerInvitation.delivered ? (
                <>
                  <p className="text-sm font-medium text-foreground">Owner invitation sent</p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {ownerEmail} has been emailed an invitation to set their own password. The
                    account cannot be signed into until they do. You did not set a password, and
                    there is none to share.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium text-foreground">
                    The company was created, but the invitation could not be emailed
                  </p>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Nothing is broken — the company and its owner exist. Open the company and use
                    “Resend invitation” once the mail problem is resolved.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-center">
          <button
            type="button"
            onClick={() => navigate(PLATFORM_ROUTES.COMPANY_DETAIL.replace(':id', result.tenantId))}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 sm:min-h-0"
          >
            Open company
          </button>
          <button
            type="button"
            onClick={() => navigate(PLATFORM_ROUTES.COMPANIES)}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0"
          >
            Back to Companies
          </button>
        </div>
      </div>
    );
  }

  // ── Review (§5.3) ─────────────────────────────────────────────────────────
  if (step === 'review') {
    return (
      <div className="mx-auto max-w-2xl space-y-5">
        <button
          onClick={() => setStep('form')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back to details
        </button>

        <div>
          <h1 className="text-xl font-semibold text-foreground">Review and create</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Creating a company provisions a live tenant, its owner account and its full role set in
            one operation. Check the details before continuing.
          </p>
        </div>

        <dl className="grid grid-cols-1 gap-4 rounded-xl border border-border bg-card p-5 sm:grid-cols-2">
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Company name</dt>
            <dd className="mt-1 break-words text-sm text-foreground">{tenantName}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Slug</dt>
            <dd className="mt-1 break-all text-sm text-foreground">{slug}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Plan</dt>
            <dd className="mt-1 text-sm text-foreground">{selectedPlan?.name ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Resulting state</dt>
            <dd className="mt-1 text-sm text-foreground">Trial</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Owner</dt>
            <dd className="mt-1 break-words text-sm text-foreground">
              {ownerFirstName} {ownerLastName}
            </dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Owner email</dt>
            <dd className="mt-1 break-all text-sm text-foreground">{ownerEmail}</dd>
          </div>
          <div>
            <dt className="text-xs font-medium text-muted-foreground">Regional</dt>
            <dd className="mt-1 text-sm text-foreground">
              {timezone} · {language} · {currency}
            </dd>
          </div>
        </dl>

        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/30 p-4">
          <FontAwesomeIcon icon={faShieldHalved} className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="text-sm text-muted-foreground">
            <span className="font-medium text-foreground">No password is being set.</span>{' '}
            {ownerEmail || 'The owner'} will be emailed an invitation and chooses their own
            password. Until they do, the account cannot be signed into — by them or by anyone else.
          </p>
        </div>

        {formError && (
          <div
            role="alert"
            className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {formError}
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => setStep('form')}
            disabled={submitting}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50 sm:min-h-0"
          >
            Edit details
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={submitting}
            className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 sm:min-h-0"
          >
            {submitting && (
              <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" />
            )}
            {submitting ? 'Creating company…' : 'Create company'}
          </button>
        </div>
      </div>
    );
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  const inputClass =
    'h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:h-10';

  const fieldError = (key: string) =>
    fieldErrors[key] ? (
      <p id={`${key}-error`} role="alert" className="mt-1 text-xs text-destructive">
        {fieldErrors[key]}
      </p>
    ) : null;

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <button
        onClick={() => navigate(PLATFORM_ROUTES.COMPANIES)}
        className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
        Back to Companies
      </button>

      <div>
        <h1 className="text-xl font-semibold text-foreground">New company</h1>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Onboard a new tenant company and invite its owner.
        </p>
      </div>

      {plansError && (
        <div
          role="alert"
          className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {plansError}
        </div>
      )}

      {formError && (
        <div
          role="alert"
          className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
        >
          {formError}
        </div>
      )}

      <form onSubmit={goToReview} className="space-y-6" noValidate>
        {/* Company */}
        <fieldset className="space-y-4 rounded-xl border border-border bg-card p-5">
          <legend className="px-1 text-sm font-semibold text-foreground">Company</legend>

          <div>
            <label
              htmlFor="tenantName"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              Company name <span className="text-destructive">*</span>
            </label>
            <input
              id="tenantName"
              type="text"
              value={tenantName}
              maxLength={255}
              onChange={(e) => setTenantName(e.target.value)}
              aria-invalid={Boolean(fieldErrors['tenantName'])}
              aria-describedby={fieldErrors['tenantName'] ? 'tenantName-error' : undefined}
              className={inputClass}
            />
            {fieldError('tenantName')}
          </div>

          <div>
            <label htmlFor="slug" className="mb-1.5 block text-sm font-medium text-foreground">
              Slug <span className="text-destructive">*</span>
            </label>
            <input
              id="slug"
              type="text"
              value={slug}
              maxLength={63}
              onChange={(e) => {
                setSlugTouched(true);
                setSlug(e.target.value.toLowerCase());
              }}
              aria-invalid={Boolean(fieldErrors['slug'])}
              aria-describedby={fieldErrors['slug'] ? 'slug-error' : 'slug-hint'}
              className={inputClass}
            />
            {fieldErrors['slug'] ? (
              fieldError('slug')
            ) : (
              <p id="slug-hint" className="mt-1 text-xs text-muted-foreground">
                Lowercase letters, digits and hyphens. Used in URLs and cannot be changed after the
                company is created.
              </p>
            )}
          </div>
        </fieldset>

        {/* Plan */}
        <fieldset className="space-y-4 rounded-xl border border-border bg-card p-5">
          <legend className="px-1 text-sm font-semibold text-foreground">Plan</legend>
          <div>
            <label htmlFor="planId" className="mb-1.5 block text-sm font-medium text-foreground">
              Subscription plan <span className="text-destructive">*</span>
            </label>
            <select
              id="planId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              aria-invalid={Boolean(fieldErrors['planId'])}
              aria-describedby={fieldErrors['planId'] ? 'planId-error' : undefined}
              className={inputClass}
            >
              <option value="">Select a plan…</option>
              {plans.map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.name}
                </option>
              ))}
            </select>
            {fieldError('planId')}
          </div>
        </fieldset>

        {/* Owner */}
        <fieldset className="space-y-4 rounded-xl border border-border bg-card p-5">
          <legend className="px-1 text-sm font-semibold text-foreground">Owner</legend>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="ownerFirstName"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                First name <span className="text-destructive">*</span>
              </label>
              <input
                id="ownerFirstName"
                type="text"
                value={ownerFirstName}
                maxLength={100}
                onChange={(e) => setOwnerFirstName(e.target.value)}
                aria-invalid={Boolean(fieldErrors['ownerFirstName'])}
                aria-describedby={
                  fieldErrors['ownerFirstName'] ? 'ownerFirstName-error' : undefined
                }
                className={inputClass}
              />
              {fieldError('ownerFirstName')}
            </div>
            <div>
              <label
                htmlFor="ownerLastName"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                Last name <span className="text-destructive">*</span>
              </label>
              <input
                id="ownerLastName"
                type="text"
                value={ownerLastName}
                maxLength={100}
                onChange={(e) => setOwnerLastName(e.target.value)}
                aria-invalid={Boolean(fieldErrors['ownerLastName'])}
                aria-describedby={fieldErrors['ownerLastName'] ? 'ownerLastName-error' : undefined}
                className={inputClass}
              />
              {fieldError('ownerLastName')}
            </div>
          </div>

          <div>
            <label
              htmlFor="ownerEmail"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              Email <span className="text-destructive">*</span>
            </label>
            <input
              id="ownerEmail"
              type="email"
              value={ownerEmail}
              autoComplete="off"
              onChange={(e) => setOwnerEmail(e.target.value)}
              aria-invalid={Boolean(fieldErrors['ownerEmail'])}
              aria-describedby={fieldErrors['ownerEmail'] ? 'ownerEmail-error' : 'ownerEmail-hint'}
              className={inputClass}
            />
            {fieldErrors['ownerEmail'] ? (
              fieldError('ownerEmail')
            ) : (
              <p id="ownerEmail-hint" className="mt-1 text-xs text-muted-foreground">
                The invitation is sent here. This address may already own another company.
              </p>
            )}
          </div>

          {/*
            No password field — D-6. This notice is not decoration: an operator
            used to admin-set passwords will look for one, and needs to know its
            absence is deliberate rather than a form that failed to load.
          */}
          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/30 p-3">
            <FontAwesomeIcon
              icon={faShieldHalved}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary"
            />
            <p className="text-xs text-muted-foreground">
              <span className="font-medium text-foreground">You do not set a password.</span> The
              owner receives an emailed invitation and chooses their own credential. Until they
              accept it, the account cannot be signed into.
            </p>
          </div>
        </fieldset>

        {/* Regional */}
        <fieldset className="space-y-4 rounded-xl border border-border bg-card p-5">
          <legend className="px-1 text-sm font-semibold text-foreground">Regional</legend>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label
                htmlFor="timezone"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                Timezone
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className={inputClass}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="language"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                Language
              </label>
              <select
                id="language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className={inputClass}
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label
                htmlFor="currency"
                className="mb-1.5 block text-sm font-medium text-foreground"
              >
                Currency
              </label>
              <select
                id="currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className={inputClass}
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </fieldset>

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={() => navigate(PLATFORM_ROUTES.COMPANIES)}
            className="min-h-11 rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted sm:min-h-0"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={plans.length === 0}
            className="min-h-11 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            Review
          </button>
        </div>
      </form>
    </div>
  );
}
